import { z } from 'zod';
import type {
  AssignmentResponse,
  AvailabilityResponse,
  LiveKitTokenResponse,
  PublicPreviewResponse,
  QueueResponse,
  RobotInfo,
} from '@/types';
import {
  AssignRequestSchema,
  AssignmentResponseSchema,
  AvailabilityResponseSchema,
  ClearQueueReminderRequestSchema,
  ChallengeResponseSchema,
  ClaimLandingTouchRequestSchema,
  ClaimLandingTouchResponseSchema,
  CreateChallengeRequestSchema,
  CreateLandingTouchRequestSchema,
  CreateLandingTouchResponseSchema,
  DeletePushSubscriptionResponseSchema,
  LiveKitTokenResponseSchema,
  PushBootstrapResponseSchema,
  PublicPreviewResponseSchema,
  QueueReminderStateSchema,
  RecordChallengeShareInitiationRequestSchema,
  RecordChallengeShareInitiationResponseSchema,
  QueueResponseSchema,
  RobotInfoSchema,
  ShareSessionResponseSchema,
  SetQueueReminderRequestSchema,
  UpsertPushSubscriptionRequestSchema,
  UpsertPushSubscriptionResponseSchema,
  parseApiResponse,
} from '@/contracts';
import type {
  AssignAttribution,
  AssignRequest,
  ChallengeResponse,
  ClearQueueReminderRequest,
  ClaimLandingTouchRequest,
  ClaimLandingTouchResponse,
  CreateChallengeRequest,
  CreateLandingTouchRequest,
  CreateLandingTouchResponse,
  DeletePushSubscriptionResponse,
  PushBootstrapResponse,
  QueueReminderState,
  RecordChallengeShareInitiationRequest,
  RecordChallengeShareInitiationResponse,
  SetQueueReminderRequest,
  ShareSessionResponse,
  UpsertPushSubscriptionRequest,
  UpsertPushSubscriptionResponse,
} from '@/contracts';
import { getTraceId } from '@/lib/logger';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');
const resolveQueueBase = () => {
  const raw = import.meta.env.VITE_QUEUE_URL;
  if (!raw) {
    throw new Error('VITE_QUEUE_URL is required to call queue endpoints.');
  }
  return raw.replace(/\/$/, '');
};
const QUEUE_BASE = resolveQueueBase();

const getCSRFToken = () => {
  const token = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith('csrftoken='));
  if (!token) {
    return null;
  }
  return decodeURIComponent(token.split('=')[1]);
};

const buildCsrfHeaders = () => {
  const csrfToken = getCSRFToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
  return headers;
};

const buildAuthHeaders = (token?: string | null) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const buildTraceHeaders = () => {
  return { 'X-Trace-Id': getTraceId() };
};

const getDeviceHint = () => {
  const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
  return coarse ? 'mobile' : 'desktop';
};

type ApiErrorCode = 'network' | 'http';

type ApiErrorOptions = {
  code: ApiErrorCode;
  message: string;
  status?: number;
};

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status?: number;

  constructor(options: ApiErrorOptions) {
    super(options.message);
    this.code = options.code;
    this.status = options.status;
  }
}

const isNetworkError = (error: unknown) => error instanceof TypeError;

const parseJsonResponse = async (response: Response, context: string): Promise<unknown> => {
  if (response.status === 204) {
    throw new ApiError({
      code: 'http',
      message: `${context} failed: empty response`,
      status: response.status,
    });
  }
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new ApiError({
      code: 'http',
      message: `${context} failed: invalid response`,
      status: response.status,
    });
  }
  return response.json();
};

const parseWithSchema = <T>(raw: unknown, schema: z.ZodType<T>, context: string): T => {
  const parsed = parseApiResponse(raw, schema, { source: 'http', type: context });
  if (!parsed.ok) {
    throw new ApiError({
      code: 'http',
      message: `${context} failed: invalid response`,
    });
  }
  return parsed.value;
};

const requestQueue = async <T>(
  path: string,
  init: RequestInit,
  context: string,
  schema: z.ZodType<T>,
  expectJson = true
): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${QUEUE_BASE}${path}`, init);
  } catch (error) {
    if (isNetworkError(error)) {
      throw new ApiError({
        code: 'network',
        message: `${context} failed: queue server is unreachable. If you are debugging, check CORS (Access-Control-Allow-Origin).`,
      });
    }
    throw error;
  }

  if (!response.ok) {
    throw new ApiError({
      code: 'http',
      message: `${context} failed: ${response.status}`,
      status: response.status,
    });
  }

  if (!expectJson) {
    return undefined as T;
  }
  const raw = await parseJsonResponse(response, context);
  return parseWithSchema(raw, schema, context);
};

const requestApi = async <T>(
  path: string,
  init: RequestInit,
  context: string,
  schema: z.ZodType<T>,
  expectJson = true
): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, init);
  } catch (error) {
    if (isNetworkError(error)) {
      throw new ApiError({
        code: 'network',
        message: `${context} failed: core api is unreachable.`,
      });
    }
    throw error;
  }

  if (!response.ok) {
    throw new ApiError({
      code: 'http',
      message: `${context} failed: ${response.status}`,
      status: response.status,
    });
  }

  if (!expectJson) {
    return undefined as T;
  }

  const raw = await parseJsonResponse(response, context);
  return parseWithSchema(raw, schema, context);
};

export const assignSession = async (
  robotId: string,
  token?: string | null,
  gameTypeKey?: string | null,
  options?: { forceLeaseRefresh?: boolean; attribution?: AssignAttribution | null }
): Promise<AssignmentResponse> => {
  const requestBody = AssignRequestSchema.parse({
    game_type_key: gameTypeKey || undefined,
    robot_id: robotId,
    force_lease_refresh: options?.forceLeaseRefresh === true || undefined,
    client_meta: {
      device: getDeviceHint(),
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    attribution: options?.attribution ?? undefined,
  } satisfies AssignRequest);
  return requestQueue<AssignmentResponse>(
    '/api/game/assign',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    },
    'Assignment',
    AssignmentResponseSchema
  );
};

export const fetchQueue = async (ticketId: string, token?: string | null): Promise<QueueResponse> => {
  const query = `?ticket_id=${encodeURIComponent(ticketId)}`;
  return requestQueue<QueueResponse>(
    `/api/game/queue${query}`,
    {
      headers: {
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Queue check',
    QueueResponseSchema
  );
};

export const fetchAvailability = async (): Promise<AvailabilityResponse> => {
  return requestApi<AvailabilityResponse>(
    '/api/teleop/availability',
    {
      cache: 'no-store',
      headers: {
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Availability check',
    AvailabilityResponseSchema
  );
};

export const fetchPublicPreview = async (): Promise<PublicPreviewResponse> => {
  return requestQueue<PublicPreviewResponse>(
    '/api/game/public-preview',
    {
      cache: 'no-store',
      headers: {
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Public preview',
    PublicPreviewResponseSchema
  );
};

export const fetchPublicViewerToken = async (
  robotId: string,
  roomId: string
): Promise<LiveKitTokenResponse> => {
  const requestBody = {
    robot_id: robotId,
    room_id: roomId,
  };

  return requestApi<LiveKitTokenResponse>(
    '/api/livekit/token/public-viewer',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    },
    'Public viewer token',
    LiveKitTokenResponseSchema
  );
};

export const fetchRobots = async (token?: string | null): Promise<RobotInfo[]> => {
  return requestApi<RobotInfo[]>(
    '/api/robot/',
    {
      headers: {
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Robot fetch',
    z.array(RobotInfoSchema)
  );
};

export const fetchSessionShare = async (
  sessionId: string,
  token?: string | null
): Promise<ShareSessionResponse> => {
  return requestApi<ShareSessionResponse>(
    `/api/player/share/sessions/${encodeURIComponent(sessionId)}`,
    {
      headers: {
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      cache: 'no-store',
      credentials: 'include',
    },
    'Share clips',
    ShareSessionResponseSchema
  );
};

export const fetchChallenge = async (publicId: string): Promise<ChallengeResponse> => {
  return requestApi<ChallengeResponse>(
    `/api/player/challenges/${encodeURIComponent(publicId)}`,
    {
      cache: 'no-store',
      headers: {
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Challenge fetch',
    ChallengeResponseSchema
  );
};

export const fetchPushBootstrap = async (
  token?: string | null
): Promise<PushBootstrapResponse> => {
  return requestApi<PushBootstrapResponse>(
    '/api/player/push/bootstrap',
    {
      headers: {
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      cache: 'no-store',
      credentials: 'include',
    },
    'Push bootstrap',
    PushBootstrapResponseSchema
  );
};

export const upsertPushSubscription = async (
  payload: UpsertPushSubscriptionRequest,
  token?: string | null
): Promise<UpsertPushSubscriptionResponse> => {
  const requestBody = UpsertPushSubscriptionRequestSchema.parse(payload);
  return requestApi<UpsertPushSubscriptionResponse>(
    '/api/player/push/subscriptions',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    },
    'Push subscription upsert',
    UpsertPushSubscriptionResponseSchema
  );
};

export const deletePushSubscription = async (
  subscriptionId: string,
  token?: string | null
): Promise<DeletePushSubscriptionResponse> => {
  return requestApi<DeletePushSubscriptionResponse>(
    `/api/player/push/subscriptions/${encodeURIComponent(subscriptionId)}`,
    {
      method: 'DELETE',
      headers: {
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
    },
    'Push subscription delete',
    DeletePushSubscriptionResponseSchema
  );
};

export const setQueueReminder = async (
  payload: SetQueueReminderRequest,
  token?: string | null
): Promise<QueueReminderState> => {
  const requestBody = SetQueueReminderRequestSchema.parse(payload);
  return requestQueue<QueueReminderState>(
    '/api/game/queue/reminder',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    },
    'Queue reminder enable',
    QueueReminderStateSchema
  );
};

export const clearQueueReminder = async (
  payload: ClearQueueReminderRequest,
  token?: string | null
): Promise<QueueReminderState> => {
  const requestBody = ClearQueueReminderRequestSchema.parse(payload);
  return requestQueue<QueueReminderState>(
    '/api/game/queue/reminder',
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    },
    'Queue reminder disable',
    QueueReminderStateSchema
  );
};

export const createChallenge = async (
  payload: CreateChallengeRequest,
  token?: string | null
): Promise<ChallengeResponse> => {
  const validatedPayload = CreateChallengeRequestSchema.parse(payload);

  return requestApi<ChallengeResponse>(
    '/api/player/share/challenges',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(validatedPayload),
    },
    'Challenge create',
    ChallengeResponseSchema
  );
};

export const recordChallengeShareInitiation = async (
  challengeId: string,
  payload: RecordChallengeShareInitiationRequest,
  token?: string | null
): Promise<RecordChallengeShareInitiationResponse> => {
  const validatedPayload = RecordChallengeShareInitiationRequestSchema.parse(payload);

  return requestApi<RecordChallengeShareInitiationResponse>(
    `/api/player/share/challenges/${encodeURIComponent(challengeId)}/initiated`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(validatedPayload),
    },
    'Challenge share initiation',
    RecordChallengeShareInitiationResponseSchema
  );
};

export const createLandingTouch = async (
  payload: CreateLandingTouchRequest
): Promise<CreateLandingTouchResponse> => {
  const validatedPayload = CreateLandingTouchRequestSchema.parse(payload);

  return requestApi<CreateLandingTouchResponse>(
    '/api/attribution/landing-touches',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(validatedPayload),
    },
    'Landing touch',
    CreateLandingTouchResponseSchema
  );
};

export const claimLandingTouch = async (
  payload: ClaimLandingTouchRequest,
  token?: string | null
): Promise<ClaimLandingTouchResponse> => {
  const validatedPayload = ClaimLandingTouchRequestSchema.parse(payload);

  return requestApi<ClaimLandingTouchResponse>(
    '/api/attribution/claims',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify(validatedPayload),
    },
    'Landing touch claim',
    ClaimLandingTouchResponseSchema
  );
};

export const fetchShareClipExport = async (
  exportUrl: string,
  token?: string | null,
  signal?: AbortSignal
): Promise<Blob> => {
  const response = await fetch(exportUrl, {
    headers: {
      ...buildAuthHeaders(token),
      ...buildTraceHeaders(),
    },
    cache: 'no-store',
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new ApiError({
      code: 'http',
      message: `Share export failed: ${response.status}`,
      status: response.status,
    });
  }
  return response.blob();
};

export const releaseSession = async (
  sessionId: string,
  robotId: string,
  token?: string | null
): Promise<void> => {
  await requestQueue<void>(
    '/api/game/release',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId, robot_id: robotId }),
      keepalive: true,
    },
    'Release session',
    z.void(),
    false
  );
};

export const releaseSessionKeepalive = (
  sessionId: string,
  robotId: string,
  token?: string | null
): void => {
  void fetch(`${QUEUE_BASE}/api/game/release`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildCsrfHeaders(),
      ...buildAuthHeaders(token),
      ...buildTraceHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ session_id: sessionId, robot_id: robotId }),
    keepalive: true,
  });
};

export const confirmReady = async (ticketId: string, token?: string | null): Promise<void> => {
  await requestQueue<void>(
    '/api/game/confirm_ready',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ ticket_id: ticketId }),
    },
    'Confirm ready',
    z.void(),
    false
  );
};

export const leaveQueue = async (ticketId: string, token?: string | null): Promise<void> => {
  await requestQueue<void>(
    '/api/game/leave',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildCsrfHeaders(),
        ...buildAuthHeaders(token),
        ...buildTraceHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ ticket_id: ticketId }),
      keepalive: true,
    },
    'Leave queue',
    z.void(),
    false
  );
};

export const leaveQueueKeepalive = (ticketId: string, token?: string | null): void => {
  void fetch(`${QUEUE_BASE}/api/game/leave`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildCsrfHeaders(),
      ...buildAuthHeaders(token),
      ...buildTraceHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ ticket_id: ticketId }),
    keepalive: true,
  });
};
